import { useState } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import {
  LayoutDashboard, Building2, FileCheck, HeartPulse, Gavel,
  Scale, MessageSquare, LogOut, Bell, Search, Plus, Menu, X, Calendar,
  Shield, FileText, ShoppingBag, Users, ChevronDown, Settings
} from "lucide-react";
import DashboardHome from "@/components/DashboardHome";
import MyProperties from "@/components/MyProperties";
import TitleReports from "@/components/TitleReports";
import CourtCases from "@/components/CourtCases";
import ValuationHealth from "@/components/ValuationHealth";
import LegalServices from "@/components/LegalServices";
import AIAssistant from "@/components/AIAssistant";
import PropertyDocuments from "@/components/PropertyDocuments";
import PIOManagement from "@/components/PIOManagement";
import PIOChat from "@/components/PIOChat";
import PropertyOnboarding from "@/components/PropertyOnboarding";
import PropertyDetail from "@/components/PropertyDetail";
import BuyerDashboard from "@/components/BuyerDashboard";
import LawyerDashboard from "@/components/LawyerDashboard";
import BrokerDashboard from "@/components/BrokerDashboard";
import EZAssureDashboard from "@/components/EZAssureDashboard";
import EZAssurePropertyView from "@/components/EZAssurePropertyView";
import AdminDashboard from "@/components/AdminDashboard";
import IssueManagement from "@/components/IssueManagement";
import PropertyAssessment from "@/components/PropertyAssessment";
import NotificationPanel from "@/components/NotificationPanel";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_9eb2c609-62e3-467e-b17e-230b32f56602/artifacts/8wo2hyy0_eZamindar%20LoGo.PNG";

const ROLE_NAV = {
  owner: [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/dashboard/assessment", label: "Health Check", icon: HeartPulse },
    { path: "/dashboard/properties", label: "My Properties", icon: Building2 },
    { path: "/dashboard/ezassure", label: "eZAssure", icon: Shield },
    { path: "/dashboard/documents", label: "Documents", icon: FileText },
    { path: "/dashboard/title-reports", label: "Title Reports", icon: FileCheck },
    { path: "/dashboard/valuation", label: "Valuation Health", icon: HeartPulse },
    { path: "/dashboard/court-cases", label: "Court Cases", icon: Gavel },
    { path: "/dashboard/pio", label: "PIO Network", icon: Shield },
    { path: "/dashboard/issues", label: "Issues", icon: Scale },
    { path: "/dashboard/legal-services", label: "Legal Services", icon: Scale },
    { path: "/dashboard/ai-assistant", label: "AI Assistant", icon: MessageSquare },
  ],
  buyer: [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/dashboard/marketplace", label: "Marketplace", icon: ShoppingBag },
    { path: "/dashboard/ai-assistant", label: "AI Assistant", icon: MessageSquare },
  ],
  lawyer: [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/dashboard/legal-console", label: "Legal Console", icon: Scale },
    { path: "/dashboard/ai-assistant", label: "AI Assistant", icon: MessageSquare },
  ],
  broker: [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/dashboard/broker-console", label: "Broker Console", icon: Users },
    { path: "/dashboard/ai-assistant", label: "AI Assistant", icon: MessageSquare },
  ],
  admin: [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/dashboard/admin", label: "Admin Panel", icon: Settings },
    { path: "/dashboard/ai-assistant", label: "AI Assistant", icon: MessageSquare },
  ],
};

const ROLE_LABELS = { owner: "Property Owner", buyer: "Buyer", lawyer: "Lawyer", broker: "Broker", admin: "Administrator" };
const ROLE_COLORS = { owner: "#D4AF37", buyer: "#556B2F", lawyer: "#F59E0B", broker: "#D4AF37", admin: "#ef4444" };

export default function Dashboard() {
  const { user, logout, API, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [roleMenuOpen, setRoleMenuOpen] = useState(false);

  const currentRole = user?.role || "owner";
  const navItems = ROLE_NAV[currentRole] || ROLE_NAV.owner;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const switchRole = async (role) => {
    try {
      const res = await fetch(`${API}/auth/role`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ role }),
      });
      if (res.ok) {
        const updated = await res.json();
        setUser(updated);
        setRoleMenuOpen(false);
        navigate("/dashboard");
      }
    } catch (e) { console.error(e); }
  };

  const isActive = (path) => {
    if (path === "/dashboard") return location.pathname === "/dashboard";
    return location.pathname.startsWith(path);
  };

  return (
    <div data-testid="dashboard-layout" className="min-h-screen bg-[#050505] flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-[#0C0D0B] border-r border-white/10 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:static lg:w-64 flex flex-col`}>
        <div className="p-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="eZamindar" className="w-9 h-9 object-contain" />
            <span className="font-serif text-lg tracking-tight text-white">
              e<span className="text-[#D4AF37]">Z</span>amindar
            </span>
          </div>
        </div>

        {/* User Profile + Role Switcher */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover border border-[#D4AF37]/30" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
                <span className="text-[#D4AF37] font-serif text-sm">{user?.name?.charAt(0) || "U"}</span>
              </div>
            )}
            <div className="min-w-0">
              <p className="text-white text-sm font-sans font-medium truncate">{user?.name || "User"}</p>
              <p className="text-white/40 text-xs font-sans">{ROLE_LABELS[currentRole]}</p>
            </div>
          </div>
          {/* Role Switcher */}
          <div className="relative">
            <button
              data-testid="role-switcher-btn"
              onClick={() => setRoleMenuOpen(!roleMenuOpen)}
              className="w-full flex items-center justify-between px-3 py-2 bg-white/5 border border-white/10 rounded-sm text-xs font-sans text-white/60 hover:border-[#D4AF37]/40 transition-colors"
            >
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: ROLE_COLORS[currentRole] }} />
                {ROLE_LABELS[currentRole]}
              </span>
              <ChevronDown className="w-3 h-3" />
            </button>
            {roleMenuOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-[#11130F] border border-white/10 rounded-sm z-50 py-1">
                {Object.entries(ROLE_LABELS).map(([role, label]) => (
                  <button
                    key={role}
                    data-testid={`switch-role-${role}`}
                    onClick={() => switchRole(role)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-sans transition-colors ${role === currentRole ? "text-[#D4AF37] bg-[#D4AF37]/5" : "text-white/50 hover:text-white hover:bg-white/5"}`}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: ROLE_COLORS[role] }} />
                    {label}
                    {role === currentRole && <span className="ml-auto text-[9px] text-[#D4AF37]">Active</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto" onClick={() => roleMenuOpen && setRoleMenuOpen(false)}>
          {navItems.map((item) => (
            <button
              key={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => { navigate(item.path); setSidebarOpen(false); setRoleMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-sans transition-colors ${
                isActive(item.path)
                  ? "bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20"
                  : "text-white/60 hover:text-white hover:bg-white/5"
              }`}
            >
              <item.icon className="w-4 h-4 shrink-0" />
              {item.label}
            </button>
          ))}
        </nav>

        {/* Logout */}
        <div className="p-3 border-t border-white/10">
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm text-white/40 hover:text-red-400 hover:bg-red-400/5 transition-colors font-sans"
          >
            <LogOut className="w-4 h-4" />
            Log Out
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen" onClick={() => roleMenuOpen && setRoleMenuOpen(false)}>
        {/* Top Bar */}
        <header className="h-14 bg-[#0C0D0B]/80 backdrop-blur-xl border-b border-white/10 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button
              data-testid="mobile-menu-btn"
              className="lg:hidden text-white/60 hover:text-white"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="font-serif text-lg text-white">Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="search-btn" className="w-8 h-8 flex items-center justify-center text-white/40 hover:text-white transition-colors">
              <Search className="w-4 h-4" />
            </button>
            <NotificationPanel />
            <button
              data-testid="add-property-btn"
              onClick={() => navigate("/dashboard/properties")}
              className="hidden sm:flex items-center gap-2 bg-[#D4AF37] text-black font-semibold text-xs py-2 px-4 rounded-sm hover:bg-[#F59E0B] transition-colors"
            >
              <Plus className="w-3 h-3" />
              New Property
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6 overflow-y-auto">
          <Routes>
            <Route index element={<DashboardHome />} />
            <Route path="properties" element={<MyProperties />} />
            <Route path="properties/onboard" element={<PropertyOnboarding />} />
            <Route path="properties/:propertyId" element={<PropertyDetail />} />
            <Route path="documents" element={<PropertyDocuments />} />
            <Route path="title-reports" element={<TitleReports />} />
            <Route path="valuation" element={<ValuationHealth />} />
            <Route path="court-cases" element={<CourtCases />} />
            <Route path="pio" element={<PIOManagement />} />
            <Route path="pio/chat/:assignmentId" element={<PIOChat />} />
            <Route path="legal-services" element={<LegalServices />} />
            <Route path="ai-assistant" element={<AIAssistant />} />
            <Route path="ezassure" element={<EZAssureDashboard />} />
            <Route path="ezassure/:propertyId" element={<EZAssurePropertyView />} />
            <Route path="marketplace" element={<BuyerDashboard />} />
            <Route path="legal-console" element={<LawyerDashboard />} />
            <Route path="broker-console" element={<BrokerDashboard />} />
            <Route path="admin" element={<AdminDashboard />} />
            <Route path="issues" element={<IssueManagement />} />
            <Route path="assessment" element={<PropertyAssessment />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
